/* eslint-disable no-nested-ternary */
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import {
  // Grid,
  Stack,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import dayjs, { Dayjs } from 'dayjs';

import axios, { endpoints } from 'src/utils/axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useBoolean } from 'src/hooks/use-boolean';
import { useEffect } from 'react';
import { DayOffType } from 'src/type/day-off-config';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  handleReload: VoidFunction;
  data?: DayOffType;
  clearSelectedData: VoidFunction;
  disabledDates: string[];
};

export default function DayOffCreateDialog({
  open,
  onClose,
  handleReload,
  data,
  clearSelectedData,
  disabledDates,
}: Props) {
  const dateError = useBoolean(false);
  const startError = useBoolean(false);
  const endError = useBoolean(false);
  const timeError = useBoolean(false);

  const defaultValues = {
    date: null as unknown as Dayjs,
    startTime: null as unknown as Dayjs,
    endTime: null as unknown as Dayjs,
    note: '',
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    watch,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    if (!values.date || !values.startTime || !values.endTime) {
      if (!values.date) dateError.onTrue();
      if (!values.startTime) startError.onTrue();
      if (!values.endTime) endError.onTrue();
      return;
    }

    try {
      const body = {
        date: values.date.format('YYYY-MM-DD'),
        startTime: values.startTime.format('HH:mm:ss'),
        endTime: values.endTime.format('HH:mm:ss'),
        note: values.note,
      };

      await axios.post(endpoints.dayOffs.createOrUpdate, body);

      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    } finally {
      reset(defaultValues);
    }
  });

  const beforeClose = () => {
    onClose();
    reset(defaultValues);
    clearSelectedData();
  };

  const handleFullDayClick = () => {
    if (!watch().date) {
      dateError.onTrue();
    } else {
      setValue('startTime', dayjs(watch().date).startOf('day'));
      setValue('endTime', dayjs(watch().date).endOf('day'));
    }
  };

  useEffect(() => {
    if (watch().date) dateError.onFalse();
    if (watch().startTime) startError.onFalse();
    if (watch().endTime) endError.onFalse();

    if (watch().startTime && watch().endTime) {
      const start = dayjs(watch().startTime);
      const end = dayjs(watch().endTime);

      if (start.isAfter(end)) {
        timeError.onTrue();
      } else {
        timeError.onFalse();
      }
    }
  }, [watch().date, watch().startTime, watch().endTime]);

  useEffect(() => {
    if (data) {
      reset({
        date: dayjs(data.date),
        startTime: dayjs(data.startTime, 'HH:mm:ss'),
        endTime: dayjs(data.endTime, 'HH:mm:ss'),
        note: data.note,
      });
    } else {
      reset(defaultValues);
    }
  }, [data]);

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างวันหยุดทำการ</DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="เลือกวันหยุด"
                  value={field.value ? dayjs(field.value) : null}
                  format="DD MMMM YYYY"
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  sx={{
                    width: 1,
                  }}
                  slotProps={{
                    textField: {
                      helperText: dateError.value ? 'กรุณาเลือกวันหยุด' : null,
                      error: dateError.value,
                    },
                  }}
                  disabled={!!data}
                  shouldDisableDate={(date) => disabledDates.includes(date.format('YYYY-MM-DD'))}
                  disablePast
                />
              )}
            />

            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เลือกเวลาเริ่ม"
                      value={getValues('startTime') ? dayjs(field.value) : null}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: {
                          helperText: startError.value ? 'กรุณาเลือกเวลาเริ่ม' : null,
                          error: startError.value,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="endTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เลือกเวลาสิ้นสุด"
                      value={getValues('endTime') ? dayjs(field.value) : null}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: {
                          helperText: endError.value
                            ? 'กรุณาเลือกเวลาสิ้นสุด'
                            : timeError.value
                              ? 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่ม'
                              : null,
                          error: endError.value || timeError.value,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              <Button variant="contained" onClick={handleFullDayClick}>
                เต็มวัน
              </Button>
            </Stack>

            <RHFTextField
              multiline
              rows={3}
              name="note"
              placeholder="หมายเหตุ"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
